import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from './ui/button';
import api from '../services/api';
import {
  Shirt,
  AlertTriangle,
  ArrowRight,
  Check,
  Loader2
} from 'lucide-react';

/**
 * UniformCampaignBanner
 * 
 * Displays a prominent banner on parent dashboard when there's an active
 * uniform campaign requiring verification/ordering.
 */
const UniformCampaignBanner = () => {
  const navigate = useNavigate();
  const [campaignData, setCampaignData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [dismissed, setDismissed] = useState(false);
  const [dismissing, setDismissing] = useState(false);

  useEffect(() => {
    checkCampaign();
  }, []);

  const checkCampaign = async () => {
    try {
      const response = await api.get('/uniform/active-campaign');
      setCampaignData(response.data);
      
      // Check if this campaign was dismissed
      if (response.data?.campaign?.id) {
        try {
          const notifRes = await api.get('/notifications?include_dismissed=true');
          const isDismissed = (notifRes.data.notifications || []).some(
            n => n.type === 'uniform_campaign' && 
                 n.reference_id === response.data.campaign.id && 
                 n.status === 'dismissed'
          );
          setDismissed(isDismissed);
        } catch (e) {
          // Ignore notification check errors
        }
      }
    } catch (error) {
      // Silently fail - no campaign banner if API fails
      console.error('Failed to check uniform campaign:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDismiss = async () => {
    if (!campaignData?.campaign?.id) return;
    setDismissing(true);
    try {
      await api.post(`/notifications/uniform_campaign/${campaignData.campaign.id}/dismiss`);
      setDismissed(true);
    } catch (error) {
      console.error('Failed to dismiss:', error);
    } finally {
      setDismissing(false);
    }
  };

  // Don't show if loading, dismissed, or no active campaign
  if (loading || dismissed || !campaignData?.has_active_campaign) {
    return null;
  }

  const { campaign, pending_players, _all_verified } = campaignData;
  const hasPending = pending_players && pending_players.length > 0;

  return (
    <div 
      className={`relative mb-6 rounded-lg border-2 overflow-hidden ${
        hasPending 
          ? 'bg-gradient-to-r from-amber-50 to-orange-50 border-amber-300' 
          : 'bg-gradient-to-r from-blue-50 to-indigo-50 border-blue-300'
      }`}
      data-testid="uniform-campaign-banner"
    >
      <div className="p-4 sm:p-6">
        <div className="flex items-start gap-4">
          {/* Icon */}
          <div className={`p-3 rounded-full ${hasPending ? 'bg-amber-100' : 'bg-blue-100'}`}>
            {hasPending ? (
              <AlertTriangle className="h-6 w-6 text-amber-600" />
            ) : (
              <Shirt className="h-6 w-6 text-blue-600" />
            )}
          </div>

          {/* Content */}
          <div className="flex-1">
            <div className="flex items-start justify-between">
              <div>
                <h3 className={`font-semibold text-lg ${hasPending ? 'text-amber-900' : 'text-blue-900'}`}>
                  {campaign.title}
                </h3>
                {campaign.description && (
                  <p className={`mt-1 text-sm ${hasPending ? 'text-amber-700' : 'text-blue-700'}`}>
                    {campaign.description}
                  </p>
                )}
              </div>
            </div>

            {/* Status */}
            <div className="mt-3 flex flex-wrap items-center gap-4">
              {hasPending ? (
                <p className="text-amber-800 font-medium">
                  {pending_players.length} child{pending_players.length > 1 ? 'ren' : ''} need{pending_players.length === 1 ? 's' : ''} uniform verification
                </p>
              ) : (
                <p className="text-blue-800">
                  All children verified! You can still order additional items.
                </p>
              )}

              {campaign.deadline && (
                <span className={`text-sm px-2 py-1 rounded ${
                  hasPending ? 'bg-amber-100 text-amber-800' : 'bg-blue-100 text-blue-800'
                }`}>
                  Deadline: {new Date(campaign.deadline).toLocaleDateString()}
                </span>
              )}
            </div>

            {/* Action Buttons */}
            <div className="mt-4 flex items-center gap-3">
              <Button
                onClick={() => navigate('/parent/uniform-verification')}
                className={hasPending 
                  ? 'bg-amber-600 hover:bg-amber-700' 
                  : 'bg-blue-600 hover:bg-blue-700'
                }
                data-testid="uniform-banner-action-btn"
              >
                {hasPending ? 'Verify Uniform Details' : 'View Uniform Orders'}
                <ArrowRight className="h-4 w-4 ml-2" />
              </Button>
              
              {/* Submitted button - always show to dismiss notification */}
              <Button
                variant="outline"
                onClick={handleDismiss}
                disabled={dismissing}
                className="border-green-300 text-green-700 hover:bg-green-50"
                data-testid="uniform-submitted-btn"
              >
                {dismissing ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <>
                    <Check className="h-4 w-4 mr-1" />
                    Submitted
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Decorative element */}
      <div className={`absolute top-0 right-0 w-32 h-32 opacity-10 ${
        hasPending ? 'text-amber-500' : 'text-blue-500'
      }`}>
        <Shirt className="w-full h-full" />
      </div>
    </div>
  );
};

export default UniformCampaignBanner;
